import React, { useEffect, useRef, useState } from 'react';
import { Box, Chip, FormControlLabel, Switch, Typography } from '@mui/material';
import { ImageIcon } from 'lucide-react';
import apiClient from '../../Services/ApiClient';
import { decode } from '../../Services/DecodeToken';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { USER_ROLE } from '../../Types';
import { LocationSelect } from '../../Components/Select/LocationSelect';
import { COLORS } from '../../Styles/Colors';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormTextField,
  FormSelect,
  FormFileUpload,
  FormField,
} from '../../Components/Form';
import { Asset } from '../AssetPage';

export interface AssetFormInput {
  name: string;
  serial_number: string;
  service_interval_months?: number;
  last_service_date?: string;
  service_due_date?: string;
  device_id?: string | null;
  ovid: string;
  barcode?: string;
  image?: File | null;
  part_number: string;
  manufacturer?: string | null;
  manufacturer_id?: string | null;
  category?: string | null;
  bin_location_id?: number | null;
  is_active: boolean;
  category_id?: number | null;
  cost?: number | null;
  invoice_number?: string | null;
}

interface AssetEditFormProps {
  onSubmit: (data: AssetFormInput) => void;
  onCancel: () => void;
  defaultValues?: Asset | null;
  /** Pre-select location (e.g. when quick-adding from a kit form). */
  initialOvid?: string;
  /** Hide the location section when location is already determined. */
  hideLocation?: boolean;
  submitLabel?: string;
}

export const AssetEditForm: React.FC<AssetEditFormProps> = ({
  onSubmit,
  onCancel,
  defaultValues,
  initialOvid,
  hideLocation = false,
  submitLabel = 'Save Asset',
}) => {
  const token: any = decode();
  const [ovs] = useGetCustomData('/api/location/user/ovs');

  const [serialError, setSerialError] = useState('');
  const [checkingSerial, setCheckingSerial] = useState(false);
  const serialCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedOvs, setSelectedOvs] = useState<string>(
    initialOvid || (defaultValues?.OVID ? String(defaultValues.OVID) : '')
  );
  const [selectedCid, setSelectedCid] = useState<number | null>(null);

  const [formData, setFormData] = useState<AssetFormInput>({
    name: defaultValues?.name || '',
    serial_number: defaultValues?.serial_number || '',
    service_interval_months: defaultValues?.service_interval_months,
    last_service_date: defaultValues?.last_service_date
      ? new Date(defaultValues.last_service_date).toISOString().split('T')[0]
      : '',
    service_due_date: defaultValues?.service_due_date || '',
    device_id: defaultValues?.device_id || defaultValues?.gps_device_id || null,
    ovid: initialOvid || (defaultValues?.OVID ? String(defaultValues.OVID) : ''),
    barcode: defaultValues?.barcode || '',
    image: null,
    category_id: defaultValues?.category_id || null,
    category: defaultValues?.category || null,
    manufacturer: defaultValues?.manufacturer || '',
    manufacturer_id: defaultValues?.manufacturer_id || '',
    part_number: defaultValues?.part_number || '',
    bin_location_id: defaultValues?.bin_location_id || null,
    is_active: defaultValues?.is_active !== undefined ? defaultValues.is_active : true,
    cost: defaultValues?.cost ?? null,
    invoice_number: defaultValues?.invoice_number ?? null,
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (selectedOvs && ovs.length > 0) {
      const selectedLocation = ovs.find((loc: any) => loc.OVID.toString() === selectedOvs.toString());
      if (selectedLocation) {
        setSelectedCid(selectedLocation.CID);
      }
    }
  }, [selectedOvs, ovs]);

  useEffect(() => {
    if (initialOvid) return;
    if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && ovs.length > 0 && !selectedOvs) {
      const defaultOvs = ovs[0].OVID;
      setSelectedOvs(defaultOvs);
      setFormData((prev) => ({ ...prev, ovid: defaultOvs }));
    }
  }, [token.roles.id, ovs, selectedOvs, initialOvid]);

  useEffect(() => {
    const fetchImage = async () => {
      if (!defaultValues?.id || imagePreview) return;

      try {
        const res = await apiClient(`/api/assets/image/${defaultValues.id}`);
        if (!res.ok) return;
        const blob = await res.blob();
        setImagePreview(URL.createObjectURL(blob));
      } catch (err) {
        console.error('Image fetch error:', err);
      }
    };

    fetchImage();
  }, [defaultValues?.id, imagePreview]);

  useEffect(() => () => {
    if (serialCheckTimeout.current) clearTimeout(serialCheckTimeout.current);
  }, []);

  const checkSerialNumber = async (serialNumber: string) => {
    if (!serialNumber.trim()) {
      setSerialError('');
      return;
    }

    setCheckingSerial(true);
    try {
      const response = await apiClient(`/api/assets/validate/${encodeURIComponent(serialNumber.trim())}`);
      const data = await response.json();
      setSerialError(data.exists ? 'Serial number already exists' : '');
    } catch (error) {
      console.error('Error checking serial number:', error);
      setSerialError('');
    } finally {
      setCheckingSerial(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? undefined : name === 'service_interval_months' ? Number(value) : value,
    }));

    if (name === 'serial_number') {
      if (serialCheckTimeout.current) clearTimeout(serialCheckTimeout.current);
      serialCheckTimeout.current = setTimeout(() => checkSerialNumber(value), 500);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, device_id: e.target.value || null }));
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    const categoryName = e.target.options[e.target.selectedIndex].text;

    setFormData((prev) => ({
      ...prev,
      category_id: categoryId === '' ? null : Number(categoryId),
      category: categoryId === '' ? null : categoryName,
    }));
  };

  const handleBinLocationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const binLocationId = e.target.value ? Number(e.target.value) : null;
    setFormData((prev) => ({ ...prev, bin_location_id: binLocationId }));
  };

  const handleLocationChange = (newOvs: string | number) => {
    const ovid = String(newOvs);
    setSelectedOvs(ovid);
    setFormData((prev) => ({
      ...prev,
      ovid,
      category_id: null,
      category: null,
      bin_location_id: null,
    }));
  };

  const handleRemoveImage = () => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setFormData((prev) => ({ ...prev, image: null }));
  };

  const [devices, loadingDevices] = useGetCustomData(`/api/gps/devices/ovs/${selectedOvs}`);
  const [assetCategory] = useGetCustomData(selectedCid ? `/api/assets/category?cid=${selectedCid}` : '');
  const [binLocations, loadingBins] = useGetCustomData(selectedOvs ? `/api/bins/${selectedOvs}` : '');

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title={defaultValues ? 'Edit Asset' : 'Add Asset'}
      subtitle="Enter asset details, service schedule, and optional GPS device assignment."
      submitLabel={submitLabel}
      onCancel={onCancel}
      disableSubmit={!formData.ovid || !!serialError}
    >
      {!defaultValues && !hideLocation && (
        <FormSection>
          <SectionTitle>Location</SectionTitle>
          <SectionDescription>Select the warehouse or job site where this asset belongs.</SectionDescription>
          <FormField label="Location" required>
            <LocationSelect ovs={ovs} selectedOvs={selectedOvs} required onChange={handleLocationChange} />
          </FormField>
        </FormSection>
      )}

      <FormSection>
        <SectionTitle>Asset Details</SectionTitle>
        <SectionDescription>Enter identification, category, and manufacturer information.</SectionDescription>
        <FormGrid>
          <FormTextField label="Description" name="name" value={formData.name || ''} onChange={handleChange} required />
          <FormTextField
            label="Serial No."
            required
            name="serial_number"
            value={formData.serial_number || ''}
            onChange={handleChange}
            error={!!serialError}
            helperText={checkingSerial ? 'Checking serial number...' : serialError || ''}
          />
          <FormTextField label="Part No." name="part_number" value={formData.part_number || ''} onChange={handleChange} />
          <FormSelect
            id="category"
            label="Category"
            name="category"
            value={formData.category_id || ''}
            onChange={handleCategoryChange}
            disabled={!selectedCid}
            placeholder={!selectedCid ? 'Select a location first' : 'Select a Category'}
          >
            {selectedCid && assetCategory && Array.isArray(assetCategory) && assetCategory.length > 0 ? (
              assetCategory.map((cat: any) => (
                <option key={cat.id} value={cat.id}>{cat.category_name}</option>
              ))
            ) : selectedCid ? (
              <option value="" disabled>No categories available for this company</option>
            ) : null}
          </FormSelect>
          <FormTextField label="Manufacturer" name="manufacturer" value={formData.manufacturer || ''} onChange={handleChange} />
          <FormTextField label="Manufacturer ID" name="manufacturer_id" value={formData.manufacturer_id || ''} onChange={handleChange} />
          <FormTextField label="Barcode" name="barcode" value={formData.barcode || ''} onChange={handleChange} />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Image</SectionTitle>
        <SectionDescription>Optional photo for mobile catalog and asset identification.</SectionDescription>
        <FormFileUpload
          accept="image/*"
          fileName={formData.image?.name}
          placeholder="Click to upload an image"
          subtext="PNG or JPG recommended"
          previewUrl={imagePreview}
          icon={<ImageIcon size={28} />}
          onChange={(file) => {
            if (file) {
              setFormData((prev) => ({ ...prev, image: file }));
              setImagePreview(URL.createObjectURL(file));
            }
          }}
          onRemove={handleRemoveImage}
        />
      </FormSection>

      <FormSection>
        <SectionTitle>Service &amp; Storage</SectionTitle>
        <SectionDescription>Configure maintenance schedule, bin location, and active status.</SectionDescription>
        <FormGrid>
          <FormTextField
            label="Service Interval (months)"
            name="service_interval_months"
            type="number"
            value={formData.service_interval_months || ''}
            onChange={handleChange}
            inputProps={{ min: 0 }}
            onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
          />
          <FormSelect
            id="bin_location"
            label="Bin Location"
            name="bin_location_id"
            value={formData.bin_location_id || ''}
            onChange={handleBinLocationChange}
            disabled={!selectedOvs || loadingBins}
            placeholder={loadingBins ? 'Loading bin locations...' : 'Select a Bin Location'}
          >
            {formData.bin_location_id && <option value="">Remove Bin Location</option>}
            {binLocations && Array.isArray(binLocations) && binLocations.length > 0 ? (
              binLocations.map((bin: any) => (
                <option key={bin.bin_location_id} value={bin.bin_location_id}>
                  {bin.bin_location_name || 'Unnamed Bin'}
                </option>
              ))
            ) : !selectedOvs ? (
              <option value="" disabled>Please select a location first</option>
            ) : (
              <option value="" disabled>No bin locations available for this location</option>
            )}
          </FormSelect>
          <FormTextField
            label="Last Service Date"
            name="last_service_date"
            type="date"
            value={formData.last_service_date || ''}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
          <FormTextField
            label="Service Due Date"
            name="service_due_date"
            type="date"
            value={formData.service_due_date || ''}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </FormGrid>

        <Box display="flex" alignItems="center" mt={1}>
          <FormControlLabel
            control={<Switch checked={formData.is_active} onChange={handleChange} name="is_active" color="primary" />}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">Status:</Typography>
                <Chip
                  label={formData.is_active ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{ fontWeight: 'bold', backgroundColor: formData.is_active ? COLORS.teal : '#9e9e9e', color: 'white' }}
                />
              </Box>
            }
          />
        </Box>
      </FormSection>

      <FormSection>
        <SectionTitle>GPS &amp; Financial</SectionTitle>
        <SectionDescription>Optionally link a GPS device and record purchase details.</SectionDescription>
        <FormGrid>
          <FormSelect
            id="devices"
            label="GPS Device"
            value={formData.device_id || ''}
            onChange={handleDeviceChange}
            disabled={loadingDevices}
            placeholder={
              loadingDevices
                ? 'Loading devices...'
                : formData.device_id
                  ? undefined
                  : 'Select a Device'
            }
          >
            {formData.device_id && <option value="">Remove Device</option>}
            {devices && Array.isArray(devices) && devices.length > 0 ? (
              devices.map((device: any) => {
                const isSelectedDevice = String(device.device_id) === String(formData.device_id ?? '');
                // Still assigned to this asset in the API until save — keep it selectable after "Remove Device"
                const isAssignedToThisAsset =
                  defaultValues?.id != null && Number(device.asset_id) === Number(defaultValues.id);
                const isAssignedElsewhere =
                  (device.asset_id != null || device.fleet_id != null) && !isAssignedToThisAsset;
                const isDisabled = isAssignedElsewhere && !isSelectedDevice;
                return (
                  <option key={device.device_id} value={device.device_id} disabled={isDisabled}>
                    {device.name || 'Unknown Device'}{isDisabled ? ' (Assigned)' : ''}
                  </option>
                );
              })
            ) : !formData.device_id ? (
              <option value="" disabled>No devices assigned for this location</option>
            ) : null}
          </FormSelect>
          <FormTextField
            label="Cost"
            name="cost"
            type="number"
            value={formData.cost ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setFormData((prev) => ({ ...prev, cost: val === '' ? null : Number(val) }));
            }}
            inputProps={{ min: 0, step: '0.01' }}
            onKeyDown={(e) => { if (e.key === '-' || e.key === 'e') e.preventDefault(); }}
            InputProps={{ startAdornment: <span style={{ marginRight: 4 }}>$</span> }}
          />
          <FormTextField
            label="Invoice Number"
            name="invoice_number"
            value={formData.invoice_number ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              setFormData((prev) => ({ ...prev, invoice_number: val === '' ? null : val }));
            }}
            inputProps={{ maxLength: 100 }}
          />
        </FormGrid>
      </FormSection>
    </Form>
  );
};
